import type { RouteRecordRaw } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { FEATURE_FLAGS } from '@teamworksdev/influencer/core';
import { accessBulletins, accessTransactions, accessTasks, editAgreements, hasExchangeWrite } from '@/permissions/exchange';
import { usePermissions } from '@/composables/usePermissions';

const routes: RouteRecordRaw[] = [
	{
		path: '/exchange',
		beforeEnter: [canAccessExchangeRoutes],
		children: [
			{
				path: 'athletes',
				name: 'ExchangeAthletes',
				component: () => import('@/views/ExchangeAthletesParent.vue'),
				redirect: { name: 'ExchangeAthletesBrowse' },
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageExchange.metaTitle',
					requiredAccess: 'hasExchangeWrite',
				},
				children: [
					{
						path: 'browse',
						name: 'ExchangeAthletesBrowse',
						component: () => import('@/views/ExchangeAthletesBrowse.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeAthletesBrowse.metaTitle',
							requiredAccess: 'hasExchangeWrite',
						},
					},
					{
						path: 'saved-lists',
						name: 'ExchangeUserLists',
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeUserLists.metaTitle',
							requiredAccess: 'hasExchangeWrite',
						},
						component: () => import('@/views/ExchangeUserLists.vue'),
					},
				],
			},
			{
				path: 'athletes/:athleteUuid',
				name: 'ExchangeAthlete',
				component: () => import('@/views/ExchangeAthlete.vue'),
				props: true,
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageExchangeAthlete.metaTitle',
					requiredAccess: 'hasExchangeWrite',
				},
			},
			{
				path: 'conversations',
				name: 'ExchangeConversations',
				component: () => import('@/views/ExchangeConversations.vue'),
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageExchangeConversations.metaTitle',
					requiredAccess: 'hasExchangeWrite',
				},
				children: [
					{
						path: ':conversationUuid',
						name: 'ExchangeConversation',
						component: () => import('@/views/ExchangeConversation.vue'),
						props: true,
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeConversations.metaTitle',
							requiredAccess: 'hasExchangeWrite',
							profileSwitchRedirect: 'ExchangeConversations',
						},
					},
				],
			},
			{
				path: 'bulletins',
				name: 'ExchangeBulletins',
				component: () => import('@/views/ExchangeBulletins.vue'),
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageExchangeBulletins.metaTitle',
					permissionRule: accessBulletins,
				},
			},
			{
				path: 'bulletins/:bulletinUuid',
				name: 'ExchangeBulletin',
				component: () => import('@/views/ExchangeBulletinParent.vue'),
				props: true,
				redirect: { name: 'ExchangeBulletinDetails' },
				children: [
					{
						path: 'details',
						name: 'ExchangeBulletinDetails',
						props: true,
						component: () => import('@/views/ExchangeBulletinDetails.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeBulletinDetails.metaTitle',
							requiredAccess: 'canAccessTransactions',
							profileSwitchRedirect: 'ExchangeBulletins',
						},
						beforeEnter: (to) => {
							const userStore = useUserStore();
							if (userStore.profileAccess.isAgent || userStore.profileAccess.isTeamUser) {
								return { name: 'ExchangeBulletins', query: { bulletinUuid: to.params.bulletinUuid } };
							}
						},
					},
					{
						path: 'applications',
						name: 'ExchangeBulletinApplicants',
						props: true,
						component: () => import('@/views/ExchangeBulletinApplicants.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeBulletinApplicants.metaTitle',
							requiredAccess: () => {
								const userStore = useUserStore();
								return userStore.profileAccess.canAccessBulletins && !userStore.profileAccess.isAgent;
							},
							profileSwitchRedirect: 'ExchangeBulletins',
						},
					},
				],
			},
			{
				path: 'transactions',
				name: 'ExchangeTransactionsParent',
				component: () => import('@/views/ExchangeTransactionsParent.vue'),
				children: [
					{
						path: '',
						name: 'ExchangeTransactions',
						component: () => import('@/views/TransactionsIndex.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeTransactions.metaTitle',
							requiredAccess: 'canAccessTransactions',
							profileSwitchRedirect: () => {
								const userStore = useUserStore();
								return userStore.profileAccess.isComplianceOfficer
									? 'ComplianceTransactions'
									: 'ExchangeTransactions';
							},
						},
						beforeEnter: [canAccessTransactions],
					},
					{
						path: 'tasks',
						name: 'ExchangeTasks',
						component: () => import('@/views/TasksIndex.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeTasks.metaTitle',
							permissionRule: accessTasks,
							profileSwitchRedirect: () => {
								const userStore = useUserStore();
								return userStore.profileAccess.isComplianceOfficer
									? 'ComplianceTasks'
									: 'ExchangeTasks';
							},
						},
					},
					{
						path: 'leaderboard',
						name: 'ExchangeTransactionsLeaderboard',
						component: () => import('@/views/ExchangeTransactionsLeaderboard.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeTransactionsLeaderboard.metaTitle',
							requiredAccess: 'canAccessTransactions',
						},
						beforeEnter: [checkForLeaderboard],
					},
					{
						path: 'agreements',
						name: 'ExchangeAgreements',
						component: () => import('@/views/AgreementsIndex.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeAgreements.metaTitle',
							requiredAccess: canAccessAgreements,
							profileSwitchRedirect: () => {
								const userStore = useUserStore();
								return userStore.profileAccess.isComplianceOfficer
									? 'ComplianceAgreements'
									: 'ExchangeAgreements';
							},
						},
					},
				],
			},
			{
				path: 'transactions/:transactionUuid',
				name: 'ExchangeTransaction',
				component: () => import('@/views/ExchangeTransaction.vue'),
				props: true,
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageTransaction.metaTitle',
					permissionRule: accessTransactions,
					profileSwitchRedirect: 'ExchangeTransactions',
				},
				beforeEnter: [canAccessTransactions],
			},
			{
				path: 'agreements/:agreementUuid',
				name: 'ExchangeAgreement',
				component: () => import('@/views/ExchangeAgreement.vue'),
				props: true,
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageAgreement.metaTitle',
					permissionRule: accessTransactions,
				},
				beforeEnter: [canAccessTransactions],
			},
			{
				path: 'create-agreement',
				name: 'ExchangeCreateAgreement',
				component: () => import('@/views/ExchangeAgreementCreate.vue'),
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageAgreementCreate.metaTitle',
					requiredLDFlags: [FEATURE_FLAGS.AGREEMENTS_V2],
					permissionRule: hasExchangeWrite,
					profileSwitchRedirect: 'ExchangeAthletes',
				},
			},
			{
				path: 'edit-agreement/:agreementUuid',
				name: 'ExchangeEditAgreement',
				component: () => import('@/views/ExchangeAgreementCreate.vue'),
				props: true,
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageAgreementEdit.metaTitle',
					requiredLDFlags: [FEATURE_FLAGS.AGREEMENTS_V2],
					permissionRule: editAgreements,
					profileSwitchRedirect: 'ExchangeAthletes',
				},
			},
			{
				path: 'settings',
				name: 'ExchangeSettings',
				component: () => import('@/views/ExchangeSettingsParent.vue'),
				redirect: { name: 'ExchangeSettingsProfile' },
				children: [
					{
						path: 'profile',
						name: 'ExchangeSettingsProfile',
						component: () => import('@/views/ExchangeSettingsProfile.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeSettingsProfile.metaTitle',
							requiredAccess: 'hasExchangeWrite',
						},
					},
					{
						path: 'payment-methods',
						name: 'ExchangeSettingsPaymentMethods',
						component: () => import('@/views/ExchangeSettingsPaymentMethods.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeSettingsPaymentMethods.metaTitle',
							requiredAccess: 'hasExchangeWrite',
						},
					}, {
						path: 'members',
						name: 'ExchangeSettingsMembers',
						component: () => import('@/views/ExchangeSettingsMembers.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeSettingsMembers.metaTitle',
							requiredAccess: 'isExchangeBrandAdmin',
						},
					},
					{
						path: 'contracts/:pathMatch(.*)*',
						name: 'ExchangeSettingsContractTemplates',
						component: () => import('@/views/ExchangeSettingsContractTemplates.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeSettingsContractTemplates.metaTitle',
							requiredAccess: 'hasExchangeWrite',
							requiredLDFlags: [FEATURE_FLAGS.AGREEMENTS_V2],
						},
					},
				],
			},
		],
	},
	{
		path: '/exchange/:schoolSlug',
		name: 'ExchangeSchoolSite',
		component: () => import('@/views/ExchangeSchoolSiteParent.vue'),
		props: true,
		children: [
			{
				path: '',
				name: 'ExchangeSchoolSiteIndex',
				component: () => import('@/views/ExchangeSchoolSiteIndex.vue'),
				meta: {
					requiresAuth: false,
					layout: 'LayoutExchangeSchoolSite',
					title: 'exchangeSchoolSite.metaTitle',
				},
			},
			{
				path: 'learn-more',
				name: 'ExchangeSchoolSiteLearnMore',
				component: () => import('@/views/ExchangeSchoolSiteLearnMore.vue'),
				meta: {
					requiresAuth: false,
					layout: 'LayoutExchangeSchoolSite',
					title: 'exchangeSchoolSite.metaTitle',
				},
			},
			{
				path: 'registration',
				name: 'ExchangeSchoolSiteRegistration',
				component: () => import('@/views/ExchangeSchoolSiteRegistration.vue'),
				meta: {
					requiresAuth: false,
					layout: 'LayoutExchangeSchoolSite',
					title: 'exchangeSchoolSite.metaTitle',
				},
			},
		],
	},
];

function canAccessExchangeRoutes() {
	const userStore = useUserStore();
	if (userStore.profileAccess.hasExchangeWrite || userStore.profileAccess.isAgent || userStore.profileAccess.isTeamUser || userStore.profileAccess.isComplianceOfficer) {
		return true;
	}

	return { name: 'NotFound' };
}

function checkForLeaderboard() {
	const userStore = useUserStore();
	if (!userStore.leaderboardEnabled) {
		return { name: 'NotFound' };
	}
}

function canAccessTransactions() {
	const { hasTransactionsAccess } = usePermissions();
	if (!hasTransactionsAccess.value) {
		return { name: 'NotFound' };
	}
}

function canAccessAgreements() {
	const userStore = useUserStore();
	return userStore.hasAgreementsAccess;
}

export default routes;
